import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  Switch,
  TablePagination,
  Snackbar,
  Alert,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import MuiModal from "../../../components/MuiModal";
import { getCmsUsers, updateCmsUserStatus, assignRoles, deleteCmsUser } from "./Helper";

const CMS_ROLES = [
  "billing-manager",
  "user-manager",
  "job-manager",
  "marketing-manager",
  "sales-access",
];

const CmsUsersList = () => {
  const context = useOutletContext();
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addRoles, setAddRoles] = useState([]);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const fetchData = (pageNum, pageSize) => {
    setLoading(true);
    getCmsUsers(pageNum + 1, pageSize)
      .then((response) => {
        const res = response.data;
        if (res?.success && res?.data) {
          setUsers(res.data || []);
          setTotal(res.meta?.total ?? 0);
        } else {
          setUsers([]);
          setTotal(0);
        }
      })
      .catch((error) => {
        console.error("Error fetching CMS users:", error);
        setUsers([]);
        setTotal(0);
        const data = error.response?.data;
        const errorMessage =
          (typeof data?.message === "string" && data.message) ||
          (typeof data?.error === "string" && data.error) ||
          error.message ||
          "Failed to fetch CMS users";
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    context(["Team Manager", "CMS Users"]);
    fetchData(page, limit);
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchData(newPage, limit);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setPage(0);
    fetchData(0, newLimit);
  };

  const handleStatusToggle = (user, checked) => {
    const cmsAccess = checked ? "Active" : "Inactive";
    setTogglingId(user.id);
    updateCmsUserStatus(user.id, cmsAccess)
      .then((response) => {
        if (response?.data?.success) {
          setUsers((prev) =>
            prev.map((u) =>
              u.id === user.id
                ? { ...u, status: checked ? "active" : "inactive" }
                : u
            )
          );
          setSnackbarMessage(
            response.data.message || "User status updated successfully"
          );
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        }
      })
      .catch((error) => {
        console.error("Error updating user status:", error);
        const data = error.response?.data;
        const errorMessage =
          (typeof data?.message === "string" && data.message) ||
          (typeof data?.error === "string" && data.error) ||
          error.message ||
          "Failed to update user status";
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      })
      .finally(() => setTogglingId(null));
  };

  const handleOpenAddModal = () => {
    setAddEmail("");
    setAddRoles([]);
    setAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
    setAddEmail("");
    setAddRoles([]);
  };

  const handleAddUser = (e) => {
    e.preventDefault();
    if (!addEmail?.trim()) {
      setSnackbarMessage("Email is required");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    if (!addRoles?.length) {
      setSnackbarMessage("Select at least one role");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    setAddSubmitting(true);
    assignRoles(addEmail.trim(), addRoles)
      .then((response) => {
        if (response?.data?.success) {
          setSnackbarMessage(
            response.data.message || "User added successfully"
          );
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          handleCloseAddModal();
          fetchData(page, limit);
        }
      })
      .catch((error) => {
        console.error("Error adding user:", error);
        const data = error.response?.data;
        const errorMessage =
          (typeof data?.message === "string" && data.message) ||
          (typeof data?.error === "string" && data.error) ||
          (Array.isArray(data?.errors) && data.errors[0]) ||
          (data && typeof data === "string" ? data : null) ||
          error.message ||
          "Failed to add user";
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      })
      .finally(() => setAddSubmitting(false));
  };

  const formatRoles = (roleArr) => {
    if (!Array.isArray(roleArr) || roleArr.length === 0) return "-";
    return roleArr.join(", ");
  };

  const handleOpenDeleteModal = (user) => {
    setUserToDelete(user);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setUserToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (!userToDelete) return;
    setDeletingId(userToDelete.id);
    deleteCmsUser(userToDelete.id)
      .then((response) => {
        if (response?.data?.success) {
          setSnackbarMessage(
            response.data.message || "CMS permissions removed successfully"
          );
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          handleCloseDeleteModal();
          fetchData(page, limit);
        }
      })
      .catch((error) => {
        console.error("Error deleting CMS user:", error);
        const data = error.response?.data;
        const errorMessage =
          (typeof data?.message === "string" && data.message) ||
          (typeof data?.error === "string" && data.error) ||
          error.message ||
          "Failed to remove CMS user";
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      })
      .finally(() => setDeletingId(null));
  };

  return (
    <div className="p-5 shadow-md bg-white">
      <div className="flex w-full justify-between items-center">
        <h3
          className="text-lg uppercase tracking-wider font-bold"
          style={{ color: "#2EBAA3" }}
        >
          CMS Users
        </h3>
        <Button
          variant="contained"
          style={{ color: "white" }}
          onClick={handleOpenAddModal}
        >
          Add User
        </Button>
      </div>
      <br />
      <div className="overflow-x-auto relative">
        <table className="w-full text-sm text-left text-gray-500">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50">
            <tr>
              <th scope="col" className="py-3 px-6 uppercase">
                Sr No.
              </th>
              <th scope="col" className="py-3 px-6 uppercase">
                Username
              </th>
              <th scope="col" className="py-3 px-6 uppercase">
                Email
              </th>
              <th scope="col" className="py-3 px-6 uppercase">
                Roles
              </th>
              <th scope="col" className="py-3 px-6 uppercase">
                Status
              </th>
              <th scope="col" className="py-3 px-6 uppercase text-center">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="py-4 px-6 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : users?.length > 0 ? (
              users.map((user, index) => (
                <tr
                  className="bg-white border-b hover:bg-gray-50"
                  key={user.id}
                >
                  <th
                    scope="row"
                    className="py-4 px-6 font-medium text-gray-900 whitespace-nowrap"
                  >
                    {page * limit + index + 1}
                  </th>
                  <td className="py-4 px-6">{user.username || "-"}</td>
                  <td className="py-4 px-6">{user.email || "-"}</td>
                  <td className="py-4 px-6">
                    {formatRoles(user.role)}
                  </td>
                  <td className="py-4 px-6">
                    <Switch
                      checked={user.status === "active"}
                      disabled={togglingId === user.id}
                      onChange={(e) =>
                        handleStatusToggle(user, e.target.checked)
                      }
                      color="primary"
                    />
                  </td>
                  <td className="py-4 px-6 text-center">
                    <IconButton
                      aria-label="delete"
                      onClick={() => handleOpenDeleteModal(user)}
                      disabled={deletingId === user.id}
                      size="small"
                      color="error"
                      title="Remove CMS access"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="6"
                  className="py-4 px-6 text-center text-gray-500"
                >
                  No CMS users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={limit}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <MuiModal
        open={deleteModalOpen}
        onCancel={handleCloseDeleteModal}
        title="Remove CMS access"
      >
        <div className="flex flex-col">
          <span className="text-base">
            Do you really want to remove CMS access for this user?
            {userToDelete && (
              <span className="block mt-2 text-gray-600">
                {userToDelete.email || userToDelete.username}
              </span>
            )}
          </span>
          <div className="flex justify-center pt-4 gap-2">
            <Button
              variant="contained"
              style={{ backgroundColor: "#d33", color: "white" }}
              onClick={handleConfirmDelete}
              disabled={deletingId === userToDelete?.id}
            >
              {deletingId === userToDelete?.id ? "Removing..." : "Yes"}
            </Button>
            <Button
              variant="contained"
              style={{ backgroundColor: "#2EBAA3", color: "white" }}
              onClick={handleCloseDeleteModal}
            >
              No
            </Button>
          </div>
        </div>
      </MuiModal>

      <MuiModal
        open={addModalOpen}
        onCancel={handleCloseAddModal}
        title="Add User"
        maxWith="sm"
      >
        <form onSubmit={handleAddUser}>
          <TextField
            fullWidth
            label="Email *"
            type="email"
            value={addEmail}
            onChange={(e) => setAddEmail(e.target.value)}
            margin="normal"
            variant="outlined"
            size="small"
            placeholder="user@example.com"
          />
          <div className="mt-3 mb-1 text-sm font-medium text-gray-700">
            Roles *
          </div>
          <div className="grid grid-cols-3 gap-x-6 gap-y-1">
            {CMS_ROLES.map((role) => (
              <FormControlLabel
                key={role}
                control={
                  <Checkbox
                    checked={addRoles.includes(role)}
                    onChange={(e) =>
                      setAddRoles((prev) =>
                        e.target.checked
                          ? [...prev, role]
                          : prev.filter((r) => r !== role)
                      )
                    }
                    size="small"
                  />
                }
                label={role}
                sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
              />
            ))}
          </div>
          <div className="text-right pt-6">
            <Button
              type="button"
              variant="outlined"
              style={{ marginRight: "10px" }}
              onClick={handleCloseAddModal}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              style={{ color: "white" }}
              disabled={addSubmitting}
            >
              {addSubmitting ? "Adding..." : "Add"}
            </Button>
          </div>
        </form>
      </MuiModal>
    </div>
  );
};

export default CmsUsersList;
